import { useEffect, useState } from "react";
import { getIn, useFormikContext } from "formik";
import { RefreshCw } from "lucide-react";
import { generatePlateformeCode } from "../api/plateformesApi";

interface CodeEntrepriseFieldProps {
  /** Nom du champ Formik. */
  name: string;
  label?: string;
  required?: boolean;
  /** Génère un code au montage si le champ est vide (création). */
  autoGenerateIfEmpty?: boolean;
}

/**
 * Champ « Code entreprise » (clé SSO) : saisissable manuellement — certaines
 * plateformes imposent leur code — ou généré via le bouton « Générer ».
 * Partagé entre la création et l'édition d'une plateforme.
 */
export const CodeEntrepriseField: React.FC<CodeEntrepriseFieldProps> = ({
  name,
  label = "Code entreprise",
  required,
  autoGenerateIfEmpty = false,
}) => {
  const { values, errors, touched, submitCount, setFieldValue, handleChange, handleBlur } =
    useFormikContext<Record<string, unknown>>();
  const [generating, setGenerating] = useState(false);

  const value = (getIn(values, name) as string) ?? '';
  const showError = submitCount > 0 || getIn(touched, name);
  const error = showError ? (getIn(errors, name) as string | undefined) : undefined;

  const generate = async () => {
    setGenerating(true);
    try {
      const code = await generatePlateformeCode();
      if (code) {
        setFieldValue(name, code);
      }
    } catch {
      /* silencieux : l'utilisateur peut saisir manuellement */
    } finally {
      setGenerating(false);
    }
  };

  useEffect(() => {
    if (autoGenerateIfEmpty && !value) {
      generate();
    }
    // au montage uniquement
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex flex-col gap-1">
      <label className="text-sm font-medium text-gray-700">
        {label}
        {required && <span className="text-red-500"> *</span>}
      </label>
      <div className="flex gap-2">
        <input
          name={name}
          value={value}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder="Ex : C2026-42"
          className="flex-1 min-w-0 rounded-lg border border-gray-300 px-4 h-12 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
        />
        <button
          type="button"
          onClick={generate}
          disabled={generating}
          className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 h-12 text-sm text-gray-700 hover:border-primary hover:text-primary transition-colors disabled:opacity-50 flex-shrink-0"
        >
          <RefreshCw size={15} className={generating ? 'animate-spin' : ''} />
          Générer
        </button>
      </div>
      {error && <p className="text-xs text-red-600">{error}</p>}
      <p className="text-xs text-gray-500">
        Clé de connexion SSO — laissez « Générer » ou saisissez le code imposé par
        la plateforme.
      </p>
    </div>
  );
};
